/**
 * The `prefers-reduced-motion: reduce` policy, as ONE CSS block every motion-bearing surface reads (DL-1.10,
 * 2026-08-20).
 *
 * WHY A GLOBAL WILDCARD RATHER THAN PER-TOKEN ZEROING. Measured before this leaf existed, the tree carried exactly
 * THREE reduced-motion guards (`components/feedback/Spinner.module.css`, `components/primitives/Switch.module.css`,
 * `spa/connect/globals.css`) against: seven runtime-injected `<style>` keyframe blocks, ten module-scoped drawer
 * animations, eighteen Tailwind `animate-*` utilities, the emitted `.fade-in` / `.shimmer` / `.status-dot` /
 * `.federa-card` utilities, and roughly 136 inline `transition:` declarations spread across the rendering tier.
 * Per-token zeroing can only neutralize what the emitting sheet NAMES -- that is the last group and nothing else, and
 * every one of the others is authored somewhere the emitter cannot see. The wildcard reaches all of them at once, and
 * it is the only arm that can, so it is the policy rather than one arm of a pair.
 *
 * `!important` IS LOAD-BEARING, not emphasis. An important AUTHOR declaration outranks a NORMAL inline `style`
 * attribute in the cascade, which is the only reason this block can reach `Modal.tsx`, `StatusDot.tsx` and every other
 * primitive that writes its animation into a `React.CSSProperties` bag. Drop the `!important` and the entire inline
 * half of the tier silently keeps animating.
 *
 * `0.01ms` RATHER THAN `0s`, also deliberate: a zero-duration animation in several engines never fires `animationend`
 * / `transitionend`, so a component that advances its own state on that event would hang forever for exactly the users
 * who asked for less motion. An almost-zero duration ends immediately AND still fires the event.
 *
 * `animation-name: none` is NOT used here: it would break the `forwards` fill of an entrance animation, leaving a
 * `opacity: 0` starting frame painted. Collapsing the duration keeps the END state.
 */

/** The media query the policy answers to -- spelled once, for the CSS block below and for the one motion the block
 *  cannot reach: a figure redrawn by a JS interval (Noor's earnings meter, `apps/noor/src/partners/tick-store.ts`),
 *  which reads it through `matchMedia` and slows its own step (critics, 2026-09-18). Module-local so the block can
 *  compose it; published as `REDUCED_MOTION.MEDIA_QUERY`. */
const MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

/** The policy, as one namespace (`QUAL-CONST-GROUP-001`): the query, the block that answers it, and the id its
 *  runtime-injected copy carries. */
export const REDUCED_MOTION = {
	/** `<style>` element id for the runtime-injected copy; one per document, checked before insert. */
	STYLE_ID: 'federa-reduced-motion',
	/** The media query the policy answers to. */
	MEDIA_QUERY,
	/**
	 * The policy block. Appended to the emitted base sheet AND injected beside any runtime-injected stylesheet, because a
	 * surface that injects its own keyframes at runtime (an MFE mounted into a host that never loaded base.css) is
	 * exactly the surface the emitted copy cannot reach.
	 */
	CSS: `@media ${MEDIA_QUERY} {
\t*,
\t*::before,
\t*::after {
\t\tanimation-duration: 0.01ms !important;
\t\tanimation-delay: 0ms !important;
\t\tanimation-iteration-count: 1 !important;
\t\ttransition-duration: 0.01ms !important;
\t\ttransition-delay: 0ms !important;
\t\tscroll-behavior: auto !important;
\t}
}`,
} as const;
